/**
 * Dashboard 360° — Visão Executiva, Produção, Compras, Comercial
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import {
  BarChart3, TrendingUp, TrendingDown, Ship, Package, ShoppingCart, Users,
  AlertTriangle, Clock, ArrowRight, DollarSign, Target, Layers,
  CheckCircle2, XCircle, Loader2, RefreshCw, Calendar as CalendarIcon, Factory,
  Truck, BarChart, PieChart as PieChartIcon, Activity
} from 'lucide-react';
import {
  BarChart as ReBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';
import { toast } from 'sonner';
import { HelpTooltip } from '../components/ui/help-tooltip';
import { format, startOfWeek, startOfMonth, startOfQuarter, subMonths, startOfYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` } });

const fmt = (v) => {
  if (!v && v !== 0) return 'R$ 0';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
const SEMAFORO = { ok: 'bg-emerald-100 text-emerald-700 border-emerald-200', atencao: 'bg-amber-100 text-amber-700 border-amber-200', critico: 'bg-red-100 text-red-700 border-red-200' };

// ============= KPI Card =============
const KpiCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue', onClick, help }) => (
  <Card className={`hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick} data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</p>
            {help && <HelpTooltip title={help.title} description={help.description} connections={help.connections} iconSize={13} />}
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-1 truncate">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2.5 rounded-xl bg-${color}-50 shrink-0`}>
          <Icon className={`h-5 w-5 text-${color}-600`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {fmtPct(Math.abs(trend))}
        </div>
      )}
    </CardContent>
  </Card>
);

// ============= Progress Bar =============
const ProgressBar = ({ value, max = 100, color = 'blue', size = 'md' }) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const h = size === 'sm' ? 'h-1.5' : 'h-2.5';
  const barColor = pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-500' : `bg-${color}-500`;
  return (
    <div className={`w-full ${h} bg-gray-100 rounded-full overflow-hidden`}>
      <div className={`${h} ${barColor} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
};

// ============= Semáforo Badge =============
const SemaforoBadge = ({ tipo, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${SEMAFORO[tipo]}`}>{children}</span>
);

// ============= Period helpers =============
const PERIOD_OPTIONS = [
  { value: 'todos', label: 'Todo período' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'mes', label: 'Este mês' },
  { value: 'trimestre', label: 'Este trimestre' },
  { value: 'semestre', label: 'Este semestre' },
  { value: 'ano', label: 'Este ano' },
  { value: 'personalizado', label: 'Personalizado' },
];

const getDateRange = (period) => {
  const now = new Date();
  switch (period) {
    case 'semana': return { from: startOfWeek(now, { weekStartsOn: 1 }), to: now };
    case 'mes': return { from: startOfMonth(now), to: now };
    case 'trimestre': return { from: startOfQuarter(now), to: now };
    case 'semestre': return { from: subMonths(startOfMonth(now), 5).getDate() === 1 ? subMonths(now, 5) : subMonths(now, 5), to: now };
    case 'ano': return { from: startOfYear(now), to: now };
    default: return { from: null, to: null };
  }
};

// ============= MAIN COMPONENT =============
const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [factories, setFactories] = useState([]);
  const [selectedFactory, setSelectedFactory] = useState('todas');
  const [activeTab, setActiveTab] = useState('executivo');
  const [period, setPeriod] = useState('todos');
  const [customRange, setCustomRange] = useState({ from: null, to: null });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const dateRange = useMemo(() => {
    if (period === 'personalizado') return customRange;
    if (period === 'todos') return { from: null, to: null };
    return getDateRange(period);
  }, [period, customRange]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedFactory !== 'todas') params.set('factory_id', selectedFactory);
      if (dateRange.from) params.set('date_from', dateRange.from.toISOString());
      if (dateRange.to) params.set('date_to', dateRange.to.toISOString());
      const qs = params.toString() ? `?${params.toString()}` : '';
      const [dashRes, factRes] = await Promise.all([
        api.get(`/dashboard/360${qs}`),
        api.get('/factories').catch(() => ({ data: [] })),
      ]);
      setData(dashRes.data);
      setFactories(Array.isArray(factRes.data) ? factRes.data : []);
    } catch (err) {
      toast.error('Erro ao carregar dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedFactory, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const exec = data?.executivo || {};
  const prod = data?.producao || {};
  const comp = data?.compras || {};
  const comer = data?.comercial || {};

  return (
    <div className="space-y-4" data-testid="dashboard-360">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard 360°</h1>
          <p className="text-sm text-gray-500">Visão consolidada do negócio</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period} onValueChange={(v) => { setPeriod(v); if (v !== 'personalizado') setCalendarOpen(false); }}>
            <SelectTrigger className="w-40" data-testid="filter-period">
              <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {period === 'personalizado' && (
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5 font-normal" data-testid="filter-custom-date">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {customRange.from ? (
                    customRange.to
                      ? `${format(customRange.from, 'dd/MM/yy')} – ${format(customRange.to, 'dd/MM/yy')}`
                      : format(customRange.from, 'dd/MM/yyyy')
                  ) : 'Selecionar datas'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end" sideOffset={4}>
                <Calendar
                  mode="range"
                  selected={customRange}
                  onSelect={(range) => {
                    setCustomRange({ from: range?.from || null, to: range?.to || null });
                    if (range?.from && range?.to) setCalendarOpen(false);
                  }}
                  locale={ptBR}
                  numberOfMonths={2}
                  defaultMonth={subMonths(new Date(), 1)}
                />
              </PopoverContent>
            </Popover>
          )}
          {period !== 'todos' && dateRange.from && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
              {format(dateRange.from, 'dd/MM/yy')} – {dateRange.to ? format(dateRange.to, 'dd/MM/yy') : 'Hoje'}
            </span>
          )}
          <Select value={selectedFactory} onValueChange={setSelectedFactory}>
            <SelectTrigger className="w-44" data-testid="filter-factory">
              <Factory className="h-4 w-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Todas as fábricas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as Fábricas</SelectItem>
              {factories.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="btn-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border shadow-sm">
          <TabsTrigger value="executivo" className="gap-1.5" data-testid="tab-executivo">
            <BarChart3 className="h-4 w-4" /> Visão Executiva
          </TabsTrigger>
          <TabsTrigger value="producao" className="gap-1.5" data-testid="tab-producao">
            <Ship className="h-4 w-4" /> Produção
          </TabsTrigger>
          <TabsTrigger value="compras" className="gap-1.5" data-testid="tab-compras">
            <ShoppingCart className="h-4 w-4" /> Compras
          </TabsTrigger>
          <TabsTrigger value="comercial" className="gap-1.5" data-testid="tab-comercial">
            <Users className="h-4 w-4" /> Comercial
          </TabsTrigger>
        </TabsList>

        {/* ===================== ABA EXECUTIVO ===================== */}
        <TabsContent value="executivo" className="mt-4 space-y-4" data-testid="panel-executivo">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard title="Carteira de Projetos" value={fmt(exec.backlog_total)} subtitle={`${exec.projetos_ativos} projetos ativos`} icon={DollarSign} color="blue" onClick={() => navigate('/projects')}
              help={{ title: 'Carteira de Projetos', description: 'Soma do valor contratual de todos os projetos ativos. Representa a receita comprometida que ainda não foi entregue.', connections: ['Projetos'] }} />
            <KpiCard title="Funil de Vendas" value={fmt(exec.pipeline_valor)} subtitle={`Ponderado: ${fmt(exec.pipeline_ponderado)}`} icon={Target} color="purple" onClick={() => navigate('/crm/pipeline')}
              help={{ title: 'Funil de Vendas', description: 'Valor total das oportunidades em andamento no funil de vendas. O valor ponderado aplica a probabilidade de fechamento de cada oportunidade.', connections: ['CRM', 'Oportunidades'] }} />
            <KpiCard title="Compras Realizadas" value={fmt(exec.total_compras)} subtitle={`Em ${comp.total_pedidos} pedidos`} icon={ShoppingCart} color="amber" onClick={() => navigate('/compras')}
              help={{ title: 'Compras Realizadas', description: 'Soma dos valores de todos os pedidos de compra (exceto cancelados). Inclui pedidos pendentes, aprovados e recebidos.', connections: ['Compras', 'Pedidos'] }} />
            <KpiCard title="Embarcações em Produção" value={exec.total_embarcacoes} subtitle={`${exec.fases_atrasadas} fases atrasadas`} icon={Ship} color="cyan"
              help={{ title: 'Embarcações em Produção', description: 'Total de embarcações vinculadas a projetos ativos. As fases atrasadas indicam etapas do cronograma cuja data prevista já passou.', connections: ['Projetos', 'Cronograma'] }} />
          </div>

          {/* Desvios */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Desvio de Prazo */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-600" /> Desvio de Prazo
                  <HelpTooltip title="Desvio de Prazo" description="Percentual de fases do cronograma que estão com a data de previsão de término ultrapassada. Verde: até 10%, Amarelo: 10-20%, Vermelho: acima de 20%." connections={['Cronograma', 'Produção']} iconSize={14} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-3">
                  <div className="text-3xl font-bold">
                    {exec.total_fases - exec.fases_atrasadas}<span className="text-lg text-gray-400">/{exec.total_fases}</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">fases no prazo</p>
                    <SemaforoBadge tipo={exec.desvio_prazo_pct > 20 ? 'critico' : exec.desvio_prazo_pct > 10 ? 'atencao' : 'ok'}>
                      {fmtPct(exec.desvio_prazo_pct)} atrasadas
                    </SemaforoBadge>
                  </div>
                </div>
                {exec.fases_atrasadas_lista?.length > 0 && (
                  <div className="space-y-1.5 mt-2">
                    {exec.fases_atrasadas_lista.slice(0, 4).map((f, i) => (
                      <div key={f.projeto_nome + f.fase_nome} className="flex items-center justify-between text-xs bg-red-50 rounded-lg px-3 py-2">
                        <div>
                          <span className="font-medium text-gray-800">{f.projeto_nome}</span>
                          <span className="text-gray-500 ml-1">— {f.fase_nome}</span>
                        </div>
                        <Badge className="bg-red-100 text-red-700 text-[10px]">{f.dias_atraso}d atraso</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Margem Estimada */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" /> Margem Estimada por Projeto
                  <HelpTooltip title="Margem Estimada" description="Diferença percentual entre o valor do contrato (budget) e o total de compras realizadas para o projeto. Quanto maior a barra, maior a margem. Verde: acima de 50%, Amarelo: 20-50%, Vermelho: abaixo de 20%." connections={['Projetos', 'Compras']} iconSize={14} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {exec.margem_projetos?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(200, exec.margem_projetos.slice(0, 8).length * 40)}>
                    <ReBarChart data={exec.margem_projetos.slice(0, 8)} layout="vertical" margin={{ left: 10, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" tickFormatter={v => `${v}%`} domain={[0, 100]} fontSize={11} />
                      <YAxis type="category" dataKey="nome" width={130} fontSize={10} tick={{ fill: '#64748b' }}
                        tickFormatter={v => v.length > 20 ? v.slice(0, 20) + '…' : v} />
                      <Tooltip formatter={v => `${v}%`} labelFormatter={l => l} />
                      <Bar dataKey="margem_pct" name="Margem" radius={[0, 4, 4, 0]}>
                        {exec.margem_projetos.slice(0, 8).map((entry, i) => (
                          <Cell key={entry.projeto_nome || i} fill={entry.margem_pct < 20 ? '#ef4444' : entry.margem_pct < 50 ? '#f59e0b' : '#10b981'} />
                        ))}
                      </Bar>
                    </ReBarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">Sem dados de margem</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Próximas Entregas */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-blue-600" /> Próximas Entregas
                <HelpTooltip title="Próximas Entregas" description="Projetos com data de entrega futura mais próxima. O indicador de dias usa semáforo: Verde (mais de 30 dias), Amarelo (8-30 dias), Vermelho (7 dias ou menos)." connections={['Projetos']} iconSize={14} />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {exec.proximas_entregas?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {exec.proximas_entregas.slice(0, 6).map(e => (
                    <div key={e.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer" onClick={() => navigate(`/projects`)}>
                      <Ship className="h-5 w-5 text-blue-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{e.nome}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <ProgressBar value={e.progresso} size="sm" />
                          <span className="text-[10px] text-gray-500 shrink-0">{Math.round(e.progresso)}%</span>
                        </div>
                      </div>
                      <SemaforoBadge tipo={e.dias_restantes <= 7 ? 'critico' : e.dias_restantes <= 30 ? 'atencao' : 'ok'}>
                        {e.dias_restantes}d
                      </SemaforoBadge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">Nenhuma entrega prevista</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===================== ABA PRODUÇÃO ===================== */}
        <TabsContent value="producao" className="mt-4 space-y-4" data-testid="panel-producao">
          {/* KPIs Produção */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard title="Projetos Ativos" value={exec.projetos_ativos} icon={Layers} color="blue"
              help={{ title: 'Projetos Ativos', description: 'Quantidade de projetos com status diferente de "concluído" ou "cancelado". Cada projeto pode conter uma ou mais embarcações.' }} />
            <KpiCard title="Embarcações em Produção" value={exec.total_embarcacoes} icon={Ship} color="cyan"
              help={{ title: 'Embarcações em Produção', description: 'Total de embarcações vinculadas a projetos ativos. Cada embarcação tem sua própria ficha técnica e cronograma.' }} />
            <KpiCard title="Fases Atrasadas" value={exec.fases_atrasadas} subtitle={`de ${exec.total_fases} fases ativas`} icon={AlertTriangle} color="red"
              help={{ title: 'Fases Atrasadas', description: 'Fases do cronograma cuja data de previsão de término já passou e o status não é "concluído". Indica gargalos na produção.', connections: ['Cronograma'] }} />
            <KpiCard title="Fases no Prazo" value={fmtPct(100 - exec.desvio_prazo_pct)} icon={CheckCircle2} color="green"
              help={{ title: 'Fases no Prazo', description: 'Percentual de fases ativas do cronograma que estão dentro da data prevista. Meta ideal: acima de 90%.' }} />
          </div>

          {/* Progresso por Projeto */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="h-4 w-4 text-blue-600" /> Progresso por Projeto
                <HelpTooltip title="Progresso por Projeto" description="Percentual de conclusão calculado pela razão de fases concluídas sobre o total de fases no cronograma. Clique no nome para detalhes." connections={['Projetos', 'Cronograma']} iconSize={14} />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {prod.progresso_projetos?.slice(0, 10).map(p => (
                  <div key={p.id} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-gray-800 truncate max-w-[200px] cursor-pointer hover:text-blue-600" onClick={() => navigate('/projects')}>
                          {p.nome}
                        </span>
                        <span className="text-xs text-gray-400">{p.cliente}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs text-gray-500">{p.fases_concluidas}/{p.total_fases} fases</span>
                        <span className="text-sm font-semibold text-gray-700 w-10 text-right">{p.progresso}%</span>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${p.progresso === 100 ? 'bg-emerald-500' : p.progresso > 50 ? 'bg-blue-500' : p.progresso > 0 ? 'bg-amber-500' : 'bg-gray-200'}`}
                        style={{ width: `${p.progresso}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Embarcações por Fase + Horas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <BarChart className="h-4 w-4 text-purple-600" /> Embarcações por Fase
                  <HelpTooltip title="Embarcações por Fase" description="Distribuição das fases ativas agrupadas por nome. As barras vermelhas indicam fases atrasadas. Permite identificar gargalos na produção." connections={['Cronograma', 'Produção']} iconSize={14} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {prod.embarcacoes_por_fase?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <ReBarChart data={prod.embarcacoes_por_fase} margin={{ left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="nome" fontSize={10} angle={-20} textAnchor="end" height={60}
                        tickFormatter={v => v.length > 18 ? v.slice(0, 18) + '…' : v} />
                      <YAxis fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="atrasadas" name="Atrasadas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </ReBarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">Sem dados de fases</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-600" /> Horas Previstas vs Realizadas
                  <HelpTooltip title="Horas Previstas vs Realizadas" description="Comparativo entre horas estimadas e efetivamente realizadas por projeto. Quando as horas realizadas ultrapassam as previstas, indica desvio de produtividade. Preencha os dados no módulo Produção." connections={['Produção']} iconSize={14} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {prod.horas_producao?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <ReBarChart data={prod.horas_producao}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="projeto" fontSize={10}
                        tickFormatter={v => v.length > 18 ? v.slice(0, 18) + '…' : v} />
                      <YAxis fontSize={11} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="estimado" name="Estimado" fill="#93c5fd" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="realizado" name="Realizado" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </ReBarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                    <Clock className="h-10 w-10 mb-2 opacity-30" />
                    <p className="text-sm">Registros de horas ainda não preenchidos</p>
                    <p className="text-xs mt-1">Preencha no módulo Produção para visualizar</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===================== ABA COMPRAS ===================== */}
        <TabsContent value="compras" className="mt-4 space-y-4" data-testid="panel-compras">
          {/* KPIs Compras */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard title="Total em Compras" value={fmt(comp.total_compras)} subtitle={`${comp.total_pedidos} pedidos`} icon={ShoppingCart} color="blue" onClick={() => navigate('/compras')}
              help={{ title: 'Total em Compras', description: 'Soma dos valores de todos os pedidos de compra (exceto cancelados). Inclui pedidos pendentes, aprovados e recebidos.', connections: ['Compras', 'Pedidos'] }} />
            <KpiCard title="Cotações Abertas" value={comp.total_cotacoes} icon={Package} color="purple"
              help={{ title: 'Cotações Abertas', description: 'Quantidade total de cotações registradas no sistema. Inclui cotações em andamento, respondidas e finalizadas.', connections: ['Compras', 'Fornecedores'] }} />
            <KpiCard title="Prazo Médio de Entrega" value={`${comp.lead_time_medio} dias`} icon={Clock} color="amber"
              help={{ title: 'Prazo Médio de Entrega', description: 'Tempo médio em dias entre a emissão do pedido de compra e o recebimento do material. Quanto menor, mais eficiente é a cadeia de suprimentos.', connections: ['Pedidos', 'Fornecedores'] }} />
            <KpiCard title="Estoque Crítico" value={`${comp.estoque_critico?.length || 0} itens`} icon={AlertTriangle} color="red" onClick={() => navigate('/produtos')}
              help={{ title: 'Estoque Crítico', description: 'Produtos cujo estoque atual está abaixo do estoque mínimo configurado. Itens zerados exigem ação imediata de reposição.', connections: ['Produtos', 'Compras'] }} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Aderência ao Orçamento */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Target className="h-4 w-4 text-blue-600" /> Aderência ao Orçamento
                  <HelpTooltip title="Aderência ao Orçamento" description="Compara o valor gasto em compras com o orçamento (budget) de cada projeto. Verde: até 70%, Amarelo: 70-90%, Vermelho: acima de 90%." connections={['Projetos', 'Compras']} iconSize={14} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {comp.aderencia_orcamento?.length > 0 ? (
                  <div className="space-y-3">
                    {comp.aderencia_orcamento.slice(0, 6).map((a, i) => (
                      <div key={a.projeto || i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-700 truncate max-w-[150px]">{a.projeto}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{fmt(a.gasto)} / {fmt(a.budget)}</span>
                            <SemaforoBadge tipo={a.pct_utilizado > 90 ? 'critico' : a.pct_utilizado > 70 ? 'atencao' : 'ok'}>
                              {fmtPct(a.pct_utilizado)}
                            </SemaforoBadge>
                          </div>
                        </div>
                        <ProgressBar value={a.pct_utilizado} size="sm" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">Sem dados de orçamento</p>
                )}
              </CardContent>
            </Card>

            {/* Status Cotações e Pedidos */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <PieChartIcon className="h-4 w-4 text-purple-600" /> Status de Cotações e Pedidos
                  <HelpTooltip title="Status de Cotações e Pedidos" description="Distribuição das cotações e pedidos de compra por status (pendente, aprovado, recebido, etc.). Permite visualizar gargalos no processo de compras." connections={['Cotações', 'Pedidos']} iconSize={14} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2 text-center">Cotações</p>
                    {comp.cotacoes_status && Object.keys(comp.cotacoes_status).length > 0 ? (
                      <ResponsiveContainer width="100%" height={140}>
                        <PieChart>
                          <Pie
                            data={Object.entries(comp.cotacoes_status).map(([k, v]) => ({ name: k, value: v }))}
                            cx="50%" cy="50%" innerRadius={30} outerRadius={55}
                            dataKey="value" paddingAngle={2}
                          >
                            {Object.keys(comp.cotacoes_status).map((_, i) => <Cell key={`cot-${i}`} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p className="text-xs text-gray-400 text-center">Sem dados</p>}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2 text-center">Pedidos</p>
                    {comp.pedidos_status && Object.keys(comp.pedidos_status).length > 0 ? (
                      <ResponsiveContainer width="100%" height={140}>
                        <PieChart>
                          <Pie
                            data={Object.entries(comp.pedidos_status).map(([k, v]) => ({ name: k, value: v }))}
                            cx="50%" cy="50%" innerRadius={30} outerRadius={55}
                            dataKey="value" paddingAngle={2}
                          >
                            {Object.keys(comp.pedidos_status).map((_, i) => <Cell key={`ped-${i}`} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p className="text-xs text-gray-400 text-center">Sem dados</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Dependência de Fornecedores */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Truck className="h-4 w-4 text-amber-600" /> Dependência de Fornecedores
                  <HelpTooltip title="Dependência de Fornecedores" description="Ranking dos fornecedores por valor total de pedidos. Concentração alta em poucos fornecedores pode representar risco operacional." connections={['Fornecedores', 'Pedidos']} iconSize={14} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {comp.dependencia_fornecedores?.length > 0 ? (
                  <div className="space-y-2">
                    {comp.dependencia_fornecedores.map((f, i) => (
                      <div key={f.nome || i} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-sm text-gray-700">{f.nome}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500">{f.qtd} pedidos</span>
                          <span className="text-sm font-medium text-gray-800">{fmt(f.valor)}</span>
                          <Badge variant="outline" className="text-xs">{fmtPct(f.pct_valor)}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">Sem dados</p>
                )}
              </CardContent>
            </Card>

            {/* Estoque Crítico */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" /> Itens em Estoque Crítico
                  <HelpTooltip title="Itens em Estoque Crítico" description="Lista de produtos com estoque abaixo do mínimo. Vermelho: estoque zerado (urgente). Amarelo: abaixo do mínimo configurado." connections={['Produtos']} iconSize={14} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {comp.estoque_critico?.length > 0 ? (
                  <div className="space-y-2">
                    {comp.estoque_critico.map((p, i) => (
                      <div key={p.sku || p.nome || i} className={`flex items-center justify-between p-2 rounded-lg ${p.zerado ? 'bg-red-50' : 'bg-amber-50'}`}>
                        <div>
                          <span className="text-sm font-medium text-gray-800">{p.nome}</span>
                          <span className="text-xs text-gray-400 ml-2">{p.sku}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <SemaforoBadge tipo={p.zerado ? 'critico' : 'atencao'}>
                            {p.estoque_atual}/{p.estoque_minimo}
                          </SemaforoBadge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-10 w-10 text-emerald-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Nenhum item em estoque crítico</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===================== ABA COMERCIAL ===================== */}
        <TabsContent value="comercial" className="mt-4 space-y-4" data-testid="panel-comercial">
          {/* KPIs Comercial */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard title="Oportunidades Ativas" value={comer.oportunidades_ativas} subtitle={`de ${comer.total_oportunidades} total`} icon={Target} color="purple" onClick={() => navigate('/crm/pipeline')}
              help={{ title: 'Oportunidades Ativas', description: 'Quantidade de oportunidades em andamento no funil de vendas (excluindo ganhas e perdidas). Reflete o volume atual de negociações.', connections: ['CRM', 'Funil'] }} />
            <KpiCard title="Valor no Funil" value={fmt(comer.pipeline_valor_total)} subtitle={`Ponderado: ${fmt(comer.pipeline_ponderado)}`} icon={DollarSign} color="blue"
              help={{ title: 'Valor no Funil', description: 'Soma dos valores de todas as oportunidades ativas. O valor ponderado multiplica cada oportunidade pela sua probabilidade de fechamento.', connections: ['Oportunidades'] }} />
            <KpiCard title="Taxa de Conversão" value={fmtPct(comer.taxa_conversao)} icon={TrendingUp} color="green"
              help={{ title: 'Taxa de Conversão', description: 'Percentual de oportunidades que foram convertidas em vendas (status "ganha") sobre o total de oportunidades finalizadas.', connections: ['CRM'] }} />
            <KpiCard title="Ticket Médio" value={fmt(comer.ticket_medio)} subtitle={`${comer.propostas_aceitas} propostas aceitas`} icon={BarChart3} color="amber"
              help={{ title: 'Ticket Médio', description: 'Valor médio das propostas aceitas. Calculado dividindo o valor total das vendas pela quantidade de propostas ganhas.', connections: ['Propostas'] }} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Pipeline por Etapa */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Layers className="h-4 w-4 text-blue-600" /> Funil por Etapa
                  <HelpTooltip title="Funil por Etapa" description="Distribuição das oportunidades por etapa do funil de vendas. Mostra quantidade e valor em cada fase, ajudando a identificar onde as oportunidades se concentram." connections={['CRM', 'Funil']} iconSize={14} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {comer.pipeline_etapas?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <ReBarChart data={comer.pipeline_etapas}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="etapa" fontSize={11} />
                      <YAxis fontSize={11} />
                      <Tooltip formatter={v => typeof v === 'number' && v > 100 ? fmt(v) : v} />
                      <Legend />
                      <Bar dataKey="qtd" name="Quantidade" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="valor" name="Valor (R$)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </ReBarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">Sem oportunidades</p>
                )}
              </CardContent>
            </Card>

            {/* Funil + Clientes */}
            <div className="space-y-4">
              {/* Funil de Propostas */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-emerald-600" /> Funil de Propostas
                    <HelpTooltip title="Funil de Propostas" description="Visualização do funil comercial mostrando a proporção entre propostas enviadas e aceitas. Quanto maior a barra de aceitas, melhor a taxa de conversão." connections={['Propostas', 'CRM']} iconSize={14} />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-full h-8 bg-blue-100 rounded flex items-center px-3">
                        <span className="text-xs font-medium text-blue-700">Enviadas</span>
                        <span className="ml-auto text-sm font-bold text-blue-900">{comer.propostas_enviadas}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 bg-emerald-100 rounded flex items-center px-3" style={{ width: `${comer.propostas_enviadas > 0 ? (comer.propostas_aceitas / comer.propostas_enviadas) * 100 : 0}%`, minWidth: '100px' }}>
                        <span className="text-xs font-medium text-emerald-700">Aceitas</span>
                        <span className="ml-auto text-sm font-bold text-emerald-900">{comer.propostas_aceitas}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Clientes por Segmento */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Users className="h-4 w-4 text-purple-600" /> Clientes por Segmento
                    <HelpTooltip title="Clientes por Segmento" description="Distribuição dos clientes cadastrados por segmento de atuação. Ajuda a identificar a concentração da carteira e oportunidades de diversificação." connections={['CRM', 'Clientes']} iconSize={14} />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {comer.clientes_segmento?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {comer.clientes_segmento.slice(0, 6).map((s, i) => (
                        <div key={s.segmento} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-gray-600 truncate">{s.segmento}</span>
                          <span className="text-xs font-bold text-gray-800 ml-auto">{s.qtd}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">Sem dados de segmento</p>
                  )}
                  {comer.total_clientes > 0 && (
                    <p className="text-xs text-center text-gray-400 mt-2">{comer.total_clientes} clientes cadastrados</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
